// SPDX-License-Identifier: Apache-2.0

import { Bytes, ethers, utils, Wallet } from "ethers";
import {
  BlockTag,
  TransactionRequest,
  TransactionResponse,
} from "@ethersproject/abstract-provider";
import { JsonRpcProvider } from "@ethersproject/providers/src.ts/json-rpc-provider";
import { Client, Transaction, Signer } from "@ckb-ccc/core";
import { Listener } from "@ethersproject/abstract-provider/src.ts";
import Address from "../api/types/address";
import { signBlob, signTransaction } from "@stellar/freighter-api";
import { Signature as DERSignature } from "@noble/secp256k1";
import { TransactionWithScriptGroups } from "../api/types/nervosTransactionTypes";
import { convertTxToCcc } from "./TransactionHelper";
import { blake2b } from "@nervosnetwork/ckb-sdk-utils";
import { ETHPoolCall } from "../api/types/liquidity";

export type AddressLike = Address | string;
const ERC20Abi = ["function balanceOf(address owner) view returns (uint256)"];

// Minimal LiquidityPool ABI. Only deposit/withdraw are needed; the backend
// returns the pool address/chainID/method/value/shares and the frontend makes
// the call directly with the owner's wallet (non-custodial).
const LiquidityPoolAbi = [
  "function deposit() payable returns (uint256)",
  "function withdraw(uint256 sharesToBurn) returns (uint256)",
];

// callLiquidityPool performs an LP deposit/withdraw contract call with the given
// signer, after verifying the wallet is on the pool's chain. Shared by the
// concrete providers below.
async function callLiquidityPool(
  signer: ethers.Signer,
  provider: ethers.providers.Provider,
  call: ETHPoolCall,
): Promise<TransactionResponse> {
  const net = await provider.getNetwork();
  if (BigInt(net.chainId) !== BigInt(call.chainID)) {
    throw new Error(
      `wallet is on chain ${net.chainId} but the pool is on ${call.chainID}`,
    );
  }
  const contract = new ethers.Contract(
    call.poolAddress,
    LiquidityPoolAbi,
    signer,
  );
  if (call.method === "deposit") {
    return contract.deposit({
      value: ethers.BigNumber.from(call.value ?? "0"),
    });
  }
  if (call.method === "withdraw") {
    return contract.withdraw(ethers.BigNumber.from(call.shares ?? "0"));
  }
  throw new Error(`unknown LP contract method ${call.method}`);
}

// The Provider provides an abstraction for the functionality of an Ethereum
// node. Is used for the client instead of requiring a concrete Provider
// implementation.
export default interface Provider {
  getChainID: () => Promise<bigint>;
  signMessage: (msg: Bytes | string) => Promise<string>;
  sendTransaction: (tx: any) => Promise<any>;
  getAddress: () => string;
  // If address is undefined, it uses the own address for the balance queries.
  getETHBalance: (
    blockTag?: BlockTag,
    address?: AddressLike,
  ) => Promise<string>;
  getERC20Balance: (
    tokenAddress: AddressLike,
    address?: AddressLike,
  ) => Promise<string>;
  onBlock: (listener: Listener) => void;
  // Performs an LP deposit/withdraw call on the LiquidityPool contract using the
  // owner's wallet.
  sendLPContractTx: (call: ETHPoolCall) => Promise<TransactionResponse>;
}

export class Web3Provider implements Provider {
  private provider: ethers.providers.Web3Provider;
  private readonly address: string;

  constructor(provider: ethers.providers.Web3Provider, address: string) {
    this.provider = provider;
    this.address = address;
  }

  public getChainID(): Promise<bigint> {
    return this.provider.getNetwork().then((net) => BigInt(net.chainId));
  }

  public signMessage(msg: Bytes | string): Promise<string> {
    const signer = this.provider!.getSigner();
    return signer.signMessage(utils.arrayify(msg));
  }

  public sendTransaction(tx: TransactionRequest): Promise<TransactionResponse> {
    const signer = this.provider!.getSigner();
    return signer.sendTransaction(tx);
  }

  public onBlock(listener: Listener) {
    this.provider.on("block", listener);
  }

  public offBlock(listener: Listener) {
    this.provider.off("block", listener);
  }

  getETHBalance(blockTag?: BlockTag, address?: AddressLike): Promise<string> {
    return this.provider
      .getBalance(address?.toString() || this.address, blockTag)
      .then((bal) => bal.toString());
  }

  getAddress(): string {
    return this.address;
  }

  getERC20Balance(
    tokenAddress: AddressLike,
    address?: AddressLike,
  ): Promise<string> {
    const erc20 = new ethers.Contract(
      tokenAddress.toString(),
      ERC20Abi,
      this.provider,
    );
    return erc20.balanceOf(address?.toString() || this.address);
  }

  sendLPContractTx(call: ETHPoolCall): Promise<TransactionResponse> {
    return callLiquidityPool(this.provider.getSigner(), this.provider, call);
  }
}

export class JSONRPCProvider implements Provider {
  private readonly provider: JsonRpcProvider;
  private wallet: Wallet;

  constructor(
    secretKey: string,
    ethURL: string,
    networkName: string,
    chainID: number,
  ) {
    this.provider = new ethers.providers.JsonRpcProvider(ethURL, {
      name: networkName,
      chainId: chainID,
    });

    this.wallet = new ethers.Wallet(secretKey, this.provider);
  }

  getChainID(): Promise<bigint> {
    return this.provider.getNetwork().then((net) => {
      return BigInt(net.chainId);
    });
  }

  getProvider(): JsonRpcProvider {
    return this.provider;
  }

  sendTransaction(tx: TransactionRequest): Promise<TransactionResponse> {
    return this.wallet.sendTransaction(tx);
  }

  signMessage(msg: Bytes | string): Promise<string> {
    return this.wallet.signMessage(utils.arrayify(msg));
  }

  onBlock(listener: Listener): void {
    this.provider.on("block", listener);
  }

  getAddress(): string {
    return this.wallet.address;
  }

  getETHBalance(blockTag?: BlockTag, address?: AddressLike): Promise<string> {
    return this.provider
      .getBalance(address?.toString() || this.wallet.address, blockTag)
      .then((bal) => bal.toString());
  }

  getERC20Balance(
    tokenAddress: AddressLike,
    address?: AddressLike,
  ): Promise<string> {
    const erc20 = new ethers.Contract(
      tokenAddress.toString(),
      ERC20Abi,
      this.provider,
    );
    return erc20.balanceOf(address?.toString() || this.wallet.address);
  }

  sendLPContractTx(call: ETHPoolCall): Promise<TransactionResponse> {
    return callLiquidityPool(this.wallet, this.provider, call);
  }
}

export class FreighterProvider {
  private address: string;
  private network: string;

  constructor(network: string, address: string) {
    this.network = network;
    this.address = address;
  }

  public getChainID(): Promise<bigint> {
    return Promise.resolve(BigInt(2));
  }

  sendTransaction(tx: string): Promise<string> {
    if (this.network === "TESTNET") {
      console.log(tx);
      return signTransaction(tx, {
        network: this.network,
        networkPassphrase: "Test SDF Network ; September 2015",
        accountToSign: this.address,
      });
    } else {
      return signTransaction(tx, {
        network: this.network,
        networkPassphrase: "Standalone Network ; February 2017",
        accountToSign: this.address,
      });
    }
  }

  signMessage(msg: Bytes | string): Promise<string> {
    return signBlob(msg.toString(), { accountToSign: this.address });
  }

  getAddress(): string {
    return this.address;
  }
}

/**
 * Converts an Ethereum-style (r,s,v) 65-byte signature into a DER-encoded signature.
 * @param ethSignature Hex string from MetaMask or Rei wallet (65 bytes, r + s + v).
 * @returns DER-encoded signature (hex string with 0x prefix), ~70–73 bytes long.
 */
export async function convertEthSignatureToDER(
  ethSignature: string,
): Promise<string> {
  const PaddedSignatureLength = 73;
  const MarkerByte = 0xff;
  const ZeroByte = 0x00;
  const sigBytes = utils.arrayify(ethSignature);
  if (sigBytes.length !== 65) {
    throw new Error("Signature must be 65 bytes long (Ethereum format).");
  }

  const r = BigInt("0x" + utils.hexlify(sigBytes.slice(0, 32)).slice(2));
  const s = BigInt("0x" + utils.hexlify(sigBytes.slice(32, 64)).slice(2));
  const sig = new DERSignature(r, s);

  const der = sig.toDERRawBytes(); // DER signature (likely 70 bytes)

  if (der.length >= PaddedSignatureLength) {
    throw new Error(
      `signature is too long. Expected at most ${PaddedSignatureLength - 1} bytes, got ${der.length} bytes`,
    );
  }

  // Now pad it manually
  const paddedSig = new Uint8Array(PaddedSignatureLength);
  paddedSig.set(der, 0);
  paddedSig[der.length] = MarkerByte;
  for (let i = der.length + 1; i < PaddedSignatureLength; i++) {
    paddedSig[i] = ZeroByte;
  }

  return utils.hexlify(paddedSig);
}

export class CkbCCCProvider {
  private signer: Signer;
  private client: Client;
  private readonly address: string;
  private lockArg: string | Uint8Array = "";

  constructor(signer: Signer, client: Client, address: string) {
    this.signer = signer;
    this.client = client;
    this.address = address;
  }

  public getAddress(): string {
    return this.address;
  }

  public async getInternalAddress(): Promise<string> {
    return await this.signer.getInternalAddress();
  }

  // getCkbAddress returns the address on Nervos, in the ckb1…/ckt1… form.
  //
  // getAddress returns the connected wallet's own address — for a MetaMask
  // signer the Ethereum one, from which this account's OmniLock script is
  // derived. That is what the protocol identifies the participant by, but it is
  // not something CKB can be sent to, so anything telling a user where to fund
  // must use this.
  public async getCkbAddress(): Promise<string> {
    return await this.signer.getRecommendedAddress();
  }

  public async getCodeHash(): Promise<string> {
    const recAddr = await this.signer.getRecommendedAddressObj();
    console.log("recAddr", recAddr);
    return recAddr.script.codeHash.replace(/^0x/, "");
  }

  // Returns the owner lock-script hash (0x-prefixed) for the connected wallet.
  // Used to query LP positions, which the backend filters by exact lock-hash
  // match — it must be the hash of the same lock used to deposit.
  public async getOwnerLockHash(): Promise<string> {
    const recAddr = await this.signer.getRecommendedAddressObj();
    return recAddr.script.hash();
  }

  // Returns the owner's full CKB address (bech32m, e.g. "ckt1...") for the
  // connected wallet. LP deposit/withdraw require this as `ownerAddress` — NOT
  // getInternalAddress(), which on the omnilock eth-auth (MetaMask) path returns
  // the mixed-case Ethereum address and the backend rejects it ("invalid owner
  // address: string not all lowercase or all uppercase").
  public async getRecommendedAddress(): Promise<string> {
    return this.signer.getRecommendedAddress();
  }

  public async signMessage(msg: Bytes | string): Promise<string> {
    return this.signer.signMessageRaw(utils.arrayify(msg));
  }

  public getChainID(): Promise<bigint> {
    return Promise.resolve(BigInt(3));
  }

  public async signTransaction(
    tx: TransactionWithScriptGroups,
  ): Promise<Transaction> {
    const converted = convertTxToCcc(tx);
    const _tx = Transaction.from(converted);

    try {
      const txSigned = await this.signer.signTransaction(_tx);
      console.log("txSigned", txSigned);
      return txSigned;
    } catch (e) {
      console.error("Failed to send transaction:", e);
      throw e;
    }
  }

  /**
   * Signs a tx WITHOUT CCC's prepareTransaction step — it signs exactly the tx
   * as given, adding only the witness, never cell-deps.
   *
   * Use this when the backend already built a complete, fully-resolved tx (LP
   * deposit/withdraw): the wallet's only job is to produce the omnilock
   * signature. The default signTransaction() runs prepareTransaction(), which
   * calls addCellDepsOfKnownScripts() and appends CCC's own hardcoded
   * system-script (omnilock/secp256k1) cell-deps. On a devnet whose deployed
   * system scripts live at different outpoints than CCC's ClientPublicDevnet
   * defaults, those extra deps change the tx_hash CCC signs over vs. the tx we
   * actually submit — so the omnilock recovers a different identity and the
   * node rejects it with ckb-auth error -31 (pubkey blake160 hash mismatch).
   */
  public async signOnlyTransaction(
    tx: TransactionWithScriptGroups,
  ): Promise<Transaction> {
    const _tx = Transaction.from(convertTxToCcc(tx));
    // Normalize the omnilock witness-lock placeholder to 85 bytes (the size of
    // the OmnilockWitnessLock the signer will fill in) so the sighash CCC signs
    // matches what the node verifies. CCC's normal prepareTransaction() does
    // this too, but it ALSO appends its own system-script cell-deps — which on
    // this devnet differ from the backend's and change the tx_hash, breaking
    // omnilock auth (error -31). So we do only the witness step here.
    const { script } = await this.signer.getRecommendedAddressObj();
    await _tx.prepareSighashAllWitness(script, 85, this.client);
    return this.signer.signOnlyTransaction(_tx);
  }

  public async signData(data: Uint8Array): Promise<string> {
    const ethSignature = await this.signer.signMessageRaw(data);
    console.log("ethSignature", ethSignature);
    return await convertEthSignatureToDER(ethSignature);
  }
  /**
   * Recovers the SEC1 uncompressed public key from a signature of a known message.
   */
  public async getPublicKey(): Promise<Uint8Array> {
    // const ETHEREUM_SIGNED_MESSAGE_PREFIX = "\x19Ethereum Signed Message:\n32";
    const message = "Get public key";
    const signature = await this.signer.signMessage(message);

    const msgHash = ethers.utils.hashMessage(message);
    const uncompressedKey = ethers.utils.recoverPublicKey(
      msgHash,
      signature.signature,
    );

    const uncompressedBytes = ethers.utils.arrayify(uncompressedKey); // 65 bytes
    const publicKeyX = uncompressedBytes.slice(1, 33);
    const publicKeyY = uncompressedBytes.slice(33, 65);

    const prefix = publicKeyY[31] % 2 === 0 ? 0x02 : 0x03;
    const compressedBytes = [prefix, ...publicKeyX];

    function formatArray(arr: Uint8Array | number[]) {
      return `[${Array.from(arr).join(" ")}]`;
    }

    console.log("Uncompressed public key:", formatArray(uncompressedBytes));
    console.log("Compressed public key:", formatArray(compressedBytes));
    const hash = blake2b(
      32,
      null,
      null,
      new TextEncoder().encode("ckb-default-hash"),
    );
    hash.update(uncompressedBytes);
    const fullHash = hash.digest("hex");
    const lockArg = fullHash.slice(0, 40);
    this.lockArg = lockArg;
    return uncompressedBytes;
  }
}
